import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Globe, Code, Copy, CheckCircle, Users, TrendingUp, 
  Settings, Eye, Mail, Phone, Palette, ExternalLink, Upload, Image as ImageIcon, X,
  QrCode, Download
} from 'lucide-react';
import axios from 'axios';

const LeadGeneration = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('setup');
  const [config, setConfig] = useState(null);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('rooftrack_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    company_name: '',
    logo_url: '',
    tagline: '',
    contact_email: '',
    contact_phone: '',
    primary_color: '#F97316',
    secondary_color: '#1E40AF',
    cta_text: 'Get Free Roof Estimate',
    features: []
  });

  useEffect(() => {
    loadConfig();
    loadLeads();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leadgen/config`, {
        params: { user_id: user.email },
        headers: getAuthHeaders()
      });
      
      // Successfully loaded existing config
      setConfig(response.data);
      setFormData({
        username: response.data.username || '',
        company_name: response.data.company_name || '',
        logo_url: response.data.logo_url || '',
        tagline: response.data.tagline || '',
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
        primary_color: response.data.primary_color || '#F97316',
        secondary_color: response.data.secondary_color || '#1E40AF',
        cta_text: response.data.cta_text || 'Get Free Roof Estimate',
        features: response.data.features || []
      });
      
      // Set logo preview if logo_url exists
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
    } catch (error) {
      // 404 means no config exists yet - this is expected for new users
      if (error.response?.status === 404) {
        console.log('No configuration found - user can create one');
        setConfig(null);
      } else {
        console.error('Error loading config:', error);
        // For other errors, still set config to null but show error
        if (error.response?.status !== 401) {
          alert('Error loading configuration. Please try again.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leadgen/leads`, {
        params: { user_id: user.email },
        headers: getAuthHeaders()
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    
    try {
      // Create FormData for file upload
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      // Upload logo via correct leadgen endpoint
      const response = await axios.post(
        `${BACKEND_URL}/api/leadgen/config/logo`,
        formDataUpload,
        {
          params: { user_id: user.email },
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update logo URL in form
      const logoUrl = response.data.logo_url;
      setFormData({ ...formData, logo_url: logoUrl });
      setLogoPreview(logoUrl);
      
      alert('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, logo_url: '' });
    setLogoPreview(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (config) {
        // Update existing config
        await axios.put(`${BACKEND_URL}/api/leadgen/config`, formData, {
          params: { user_id: user.email },
          headers: getAuthHeaders()
        });
        alert('Configuration updated successfully!');
      } else {
        // Create new config
        const response = await axios.post(`${BACKEND_URL}/api/leadgen/config`, formData, {
          params: { user_id: user.email },
          headers: getAuthHeaders()
        });
        setConfig(response.data);
        alert('Landing page created successfully!');
      }
      await loadConfig();
    } catch (error) {
      console.error('Save error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save configuration';
      alert(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const loadQrCode = async () => {
    if (!config?.username) return;
    setLoadingQr(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leadgen/qr-code-data/${config.username}`);
      setQrCodeData(response.data);
    } catch (error) {
      console.error('Error loading QR code:', error);
    } finally {
      setLoadingQr(false);
    }
  };

  const downloadQrCode = () => {
    if (!config?.username) return;
    // Open download URL in new tab
    window.open(`${BACKEND_URL}/api/leadgen/qr-code/${config.username}?size=500`, '_blank');
  };

  // Load QR code when embed tab is active and config exists
  useEffect(() => {
    if (activeTab === 'embed' && config?.username && !qrCodeData) {
      loadQrCode();
    }
  }, [activeTab, config]);

  const getEmbedCode = () => {
    if (!config) return '';
    // Use the current window location origin for the embed URL
    const baseUrl = window.location.origin;
    return `<iframe 
  src="${baseUrl}/leadgen/${config.username}" 
  width="100%" 
  height="600" 
  frameborder="0"
  style="border: none; border-radius: 8px;"
></iframe>`;
  };

  const getLandingPageUrl = () => {
    if (!config) return '';
    // Use the current window location origin for the landing page URL
    const baseUrl = window.location.origin;
    return `${baseUrl}/leadgen/${config.username}`;
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'embed') {
      setCopiedEmbed(true);
      setTimeout(() => setCopiedEmbed(false), 2000);
    } else {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <Globe className="w-8 h-8 mr-3" />
              Lead Generation System
            </h1>
            <p className="mt-2 text-orange-100">
              Create branded landing pages and capture leads with automatic satellite roof estimates
            </p>
          </div>
          {config && (
            <div className="text-right">
              <div className="text-3xl font-bold">{leads.length}</div>
              <div className="text-sm text-orange-100">Total Leads</div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('setup')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'setup'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Setup
            </button>
            <button
              onClick={() => setActiveTab('embed')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'embed'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              disabled={!config}
            >
              <Code className="w-4 h-4 inline mr-2" />
              Embed Code
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leads'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              disabled={!config}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Leads ({leads.length})
            </button>
          </nav>
        </div>

        {/* Setup Tab */}
        {activeTab === 'setup' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
                
                <div>
                  <Label htmlFor="username">Username (URL)</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '') })}
                    placeholder="joes-roofing"
                    disabled={!!config}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.username && `Your page: ${window.location.origin}/leadgen/${formData.username}`}
                  </p>
                  {config && (
                    <p className="text-xs text-orange-600 mt-1">
                      Username cannot be changed after creation
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Joe's Home Service Company"
                  />
                </div>

                <div>
                  <Label htmlFor="logo_url">Company Logo</Label>
                  
                  {/* Logo Preview */}
                  {(formData.logo_url || logoPreview) && (
                    <div className="mb-3 relative inline-block">
                      <img 
                        src={formData.logo_url || logoPreview} 
                        alt="Company Logo" 
                        className="h-24 w-auto border border-gray-300 rounded-lg object-contain bg-white p-2"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2 mb-2">
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      <div className={`flex items-center justify-center px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                        uploadingLogo 
                          ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
                          : 'border-blue-300 hover:border-blue-500 hover:bg-blue-50'
                      }`}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </div>
                    </label>
                  </div>
                  
                  {/* Or enter URL */}
                  <div className="relative">
                    <div className="text-xs text-gray-500 text-center mb-2">OR enter URL</div>
                    <Input
                      id="logo_url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2">
                    <ImageIcon className="w-3 h-3 inline mr-1" />
                    Recommended: PNG with transparent background, 200-400px wide, under 5MB
                  </p>
                </div>

                <div>
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input
                    id="tagline"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    placeholder="Your Trusted Roofing Partner"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Branding */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Branding</h3>

                <div>
                  <Label htmlFor="primary_color">Primary Color</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      id="primary_color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="h-10 w-20 rounded border border-gray-300"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      placeholder="#F97316"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="secondary_color">Secondary Color</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      id="secondary_color"
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      className="h-10 w-20 rounded border border-gray-300"
                    />
                    <Input
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      placeholder="#1E40AF"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cta_text">Call-to-Action Button Text</Label>
                  <Input
                    id="cta_text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="Get Free Roof Estimate"
                  />
                </div>

                {/* Preview */}
                <div className="mt-6 p-4 border rounded-lg" style={{ 
                  backgroundColor: `${formData.primary_color}10`,
                  borderColor: formData.primary_color
                }}>
                  <h4 className="font-semibold mb-2">Preview</h4>
                  <button 
                    className="px-6 py-3 rounded-lg font-semibold text-white"
                    style={{ backgroundColor: formData.primary_color }}
                  >
                    {formData.cta_text}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={isSaving || !formData.username || !formData.company_name}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isSaving ? 'Saving...' : config ? 'Update Configuration' : 'Create Landing Page'}
              </Button>
            </div>
          </div>
        )}

        {/* Embed Code Tab */}
        {activeTab === 'embed' && config && (
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Landing Page URL</h3>
                <div className="flex items-center space-x-2">
                  <Input
                    value={getLandingPageUrl()}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(getLandingPageUrl(), 'url')}
                  >
                    {copiedUrl ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(getLandingPageUrl(), '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Share this URL with customers or use it in your marketing materials
                </p>
              </div>

              {/* QR Code Section */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <QrCode className="w-5 h-5 mr-2 text-purple-600" />
                  QR Code for Marketing
                </h3>
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    {loadingQr ? (
                      <div className="w-40 h-40 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                      </div>
                    ) : qrCodeData ? (
                      <img 
                        src={qrCodeData.qr_code_url} 
                        alt="QR Code" 
                        className="w-40 h-40"
                      />
                    ) : (
                      <div className="w-40 h-40 flex items-center justify-center bg-gray-100 rounded">
                        <QrCode className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 mb-4">
                      Download your personalized QR code to use on:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-2 mb-4">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Business cards
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Flyers and brochures
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Vehicle wraps and signage
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Website or social media
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Email signatures
                      </li>
                    </ul>
                    <Button 
                      onClick={downloadQrCode}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download QR Code (PNG)
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      High-resolution 500×500px image
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Embed Code for Your Website</h3>
                <div className="space-y-2">
                  <textarea
                    value={getEmbedCode()}
                    readOnly
                    rows={6}
                    className="w-full p-3 border rounded-lg font-mono text-sm bg-gray-50"
                  />
                  <Button
                    onClick={() => copyToClipboard(getEmbedCode(), 'embed')}
                    className="w-full"
                  >
                    {copiedEmbed ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Copied to Clipboard!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Embed Code
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Paste this code into your website where you want the estimate form to appear
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">How It Works:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Customer enters their address on your landing page</li>
                  <li>System generates automatic satellite roof measurements</li>
                  <li>Instant estimate is created and shown to customer</li>
                  <li>Lead is captured and you receive email notification</li>
                  <li>View and manage all leads in the "Leads" tab</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && config && (
          <div className="p-6">
            {leads.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Leads Yet</h3>
                <p className="text-gray-600">
                  Share your landing page URL or embed the form on your website to start capturing leads
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Captured Leads</h3>
                  <div className="text-sm text-gray-500">{leads.length} total</div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estimate</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.lead_name || lead.customer_name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{lead.address || lead.property_address || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{lead.lead_email || lead.email || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{lead.lead_phone || lead.phone || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              lead.status === 'new' ? 'bg-green-100 text-green-800' :
                              lead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                              lead.status === 'qualified' ? 'bg-purple-100 text-purple-800' :
                              lead.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {lead.status || 'pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {lead.estimate_id ? (
                              <button
                                onClick={() => navigate(`/dashboard/estimates/${lead.estimate_id}`)}
                                className="inline-flex items-center px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-md transition-colors"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View Estimate
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">No estimate</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadGeneration;
